import type { Position } from "geojson";

/** Coordinates are written to Airtable at 6 decimal places — ~0.1 m, well past what a pull-in
 *  point needs, and enough that a parse → format round-trip is lossless for real values. */
const COORD_PRECISION = 6;

/**
 * A job's pull-in point — where the robot enters the field. Persisted as `Jobs.Location`, a
 * `"lat,lng"` single-line text field.
 *
 * Immutable, equality by value, self-validating: the constructor rejects anything outside the
 * WGS-84 bounds or non-finite, so a `PullInPoint` that exists is always a point that can be put
 * on a map. `parse` is the lenient door for untrusted strings — it returns null rather than
 * throwing, because Airtable's field is free text and junk in it is data, not a programming error.
 *
 * Note the axis order trap: Airtable stores `"lat,lng"`, GeoJSON wants `[lng, lat]`. `format()`
 * and `toPosition()` name which one you are getting.
 */
export class PullInPoint {
	readonly lat: number;
	readonly lng: number;

	constructor(lat: number, lng: number) {
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			throw new RangeError(`PullInPoint requires finite coordinates, got (${lat}, ${lng})`);
		}
		if (Math.abs(lat) > 90) throw new RangeError(`Latitude out of range: ${lat}`);
		if (Math.abs(lng) > 180) throw new RangeError(`Longitude out of range: ${lng}`);
		this.lat = lat;
		this.lng = lng;
	}

	/** Parse a `Jobs.Location` string. Returns null for anything that isn't exactly two in-range
	 *  finite numbers — MC parses these blindly (`SchedulingMap.tsx:234`); we do not. */
	static parse(location: string | undefined): PullInPoint | null {
		if (!location) return null;
		const parts = location.split(",");
		if (parts.length !== 2) return null;
		const latText = parts[0].trim();
		const lngText = parts[1].trim();
		if (latText === "" || lngText === "") return null;
		const lat = Number(latText);
		const lng = Number(lngText);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
		if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
		return new PullInPoint(lat, lng);
	}

	/** Build from a GeoJSON position — `[lng, lat]`, the order a map click gives you. */
	static fromPosition(position: Position): PullInPoint {
		return new PullInPoint(position[1], position[0]);
	}

	/** The Airtable wire format: `"lat,lng"`. */
	format(): string {
		return `${round(this.lat)},${round(this.lng)}`;
	}

	/** GeoJSON axis order: `[lng, lat]`. */
	toPosition(): Position {
		return [this.lng, this.lat];
	}

	equals(other: PullInPoint): boolean {
		return this.lat === other.lat && this.lng === other.lng;
	}
}

function round(n: number): number {
	return Number(n.toFixed(COORD_PRECISION));
}
